import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Building,
  Smartphone,
  Clock,
  CheckCircle,
  XCircle,
  Info,
  Calendar,
  Monitor,
  AlertTriangle,
  Play,
} from 'lucide-react'
import { DeviceData } from '../columns'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import {
  getDeviceConnectionState,
  getConnectionDisplay,
} from '@/utils/deviceConnectionStatus'

interface DeviceDetailDialogProps {
  device: DeviceData | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export default function DeviceDetailDialog({
  device,
  open,
  onOpenChange,
}: DeviceDetailDialogProps) {
  if (!device) return null

  // 使用共用工具函式判斷連線狀態
  const { state: connectionState, lastConnectedAt } = getDeviceConnectionState(
    false, // REST API 無法取得即時在線狀態
    device.last_connected_at,
  )
  const connectionDisplay = getConnectionDisplay(connectionState)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            裝置詳細資訊
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* 基本資訊 */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">基本資訊</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4" />
                  裝置 ID
                </div>
                <p className="font-medium">{device.broadcast_device_id}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Smartphone className="h-4 w-4" />
                  裝置名稱
                </div>
                <p className="font-medium">{device.name || '未命名裝置'}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Monitor className="h-4 w-4" />
                  裝置類型
                </div>
                <Badge variant="secondary">
                  {device.broadcast_type === 'LIVE'
                    ? '即時播放'
                    : device.broadcast_type === 'VOD'
                      ? '影片播放'
                      : device.broadcast_type}
                </Badge>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  {device.is_active ? (
                    <CheckCircle className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  狀態
                </div>
                <Badge variant={device.is_active ? 'default' : 'secondary'}>
                  {device.is_active ? '啟用' : '停用'}
                </Badge>
              </div>
            </div>
          </div>

          <Separator />

          {/* 診所資訊 */}
          {device.clinic && (
            <>
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">所屬診所</h3>
                <div className="flex items-center gap-2">
                  <Building className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{device.clinic.clinic_name}</span>
                  {/* <Badge variant="outline">ID: {device.clinic.clinic_id}</Badge> */}
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* 版本與連線資訊 */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">版本與連線</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Info className="h-4 w-4" />
                  APP 版本
                </div>
                <p className="font-mono text-sm">{device.app_version || '未知版本'}</p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  最後連線
                </div>
                <div className="flex items-center gap-1">
                  <span className={`inline-block w-2 h-2 rounded-full ${connectionDisplay.dotClass}`} />
                  <p className={`font-medium ${connectionDisplay.textClass}`}>
                    {lastConnectedAt ? connectionDisplay.text : '從未連線'}
                  </p>
                  {connectionDisplay.showWarning && (
                    <AlertTriangle className="h-3 w-3 text-orange-500" />
                  )}
                </div>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Play className="h-4 w-4" />
                  最後播放
                </div>
                <p className="font-medium">
                  {device.last_played_at
                    ? format(new Date(device.last_played_at), 'yyyy/MM/dd HH:mm:ss', {
                        locale: zhTW,
                      })
                    : '從未播放'}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* 時間戳記 */}
          <div>
            <h3 className="text-sm font-medium text-muted-foreground mb-3">時間記錄</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  建立時間
                </div>
                <p>
                  {device.created_at
                    ? format(new Date(device.created_at), 'yyyy/MM/dd HH:mm', { locale: zhTW })
                    : '未知'}
                </p>
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  更新時間
                </div>
                <p>
                  {device.updated_at
                    ? format(new Date(device.updated_at), 'yyyy/MM/dd HH:mm', { locale: zhTW })
                    : '未知'}
                </p>
              </div>
            </div>
          </div>

          {/* 連線詳情 */}
          {lastConnectedAt && (
            <>
              <Separator />
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">連線詳情</h3>
                <div className={`rounded-lg p-4 ${connectionDisplay.showWarning ? 'bg-orange-50' : 'bg-muted/50'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${connectionDisplay.dotClass}`} />
                      <span className="text-sm font-medium">{connectionDisplay.text}</span>
                      {connectionDisplay.showWarning && (
                        <AlertTriangle className="h-4 w-4 text-orange-500" />
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground">
                      最後連線：
                      {format(new Date(lastConnectedAt), 'yyyy/MM/dd HH:mm:ss', {
                        locale: zhTW,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
